import { Chip } from '@mdvip/shared/components/blocks/Chip';
import type { Facet, FacetValue } from '@mdvip/shared/components/blocks/ContentFilters';
import { ContentFilters, countSelected } from '@mdvip/shared/components/blocks/ContentFilters';
import { ContentFiltersDrawer } from '@mdvip/shared/components/blocks/ContentFiltersDrawer';
import type { IconName } from '@mdvip/shared/components/blocks/Icon';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Button } from '@mdvip/shared/components/ui/button';
import { Input } from '@mdvip/shared/components/ui/input';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  paginationRange,
} from '@mdvip/shared/components/ui/pagination';
import { Skeleton } from '@mdvip/shared/components/ui/skeleton';
import { cn } from '@mdvip/shared/lib/utils';
import { useLocale } from '@mdvip/shared/stores/userPreferencesStore';
import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { ContentCardProps } from './ContentCard';
import { ContentCard } from './ContentCard';

const DEFAULT_PAGE_SIZE = 9;
const SKELETON_KEYS = Array.from({ length: DEFAULT_PAGE_SIZE }, (_, i) => `skeleton-${i}`);

/** One removable pill in the active-filter row — either a facet term or a caller-owned extra (e.g. the search term). */
export interface ContentListingChip {
  key: string;
  label: React.ReactNode;
  icon?: IconName;
  onRemove: () => void;
  srLabel?: string;
}

export interface ContentListingProps {
  /** Plural noun for the count line and loading label (e.g. "recipes"). */
  noun: string;
  /** Singular form of `noun`, used by the count line's ICU plural when exactly one card is shown. Defaults to `noun`. */
  nounSingular?: string;
  title: string;
  dek?: string;
  above?: React.ReactNode;
  /**
   * Page furniture that belongs with the heading (a disclaimer, a featured card): rendered after the
   * title/dek/search lockup, before the filters and results.
   */
  belowHead?: React.ReactNode;
  /**
   * Renders the search field, baseline-aligned beside the title at `desktop:` width and full-width
   * above it below that. Omit to leave the title lockup search-free (search is one field per
   * listing, not a generic slot — `primaryControls`/`trailingControls` are for the caller's own controls).
   */
  searchLabel?: string;
  q?: string;
  onQChange?: (value: string) => void;
  /** Taxonomy facets — omit (or pass `[]`) for a listing with no filter panel at all. */
  facets?: Facet[];
  value?: FacetValue;
  onFacetChange?: (facetId: string, values: string[]) => void;
  cards: ContentCardProps[];
  filteredCount: number;
  loading?: boolean;
  emptyIcon?: IconName;
  emptyHint: string;
  /** Overrides the empty-state heading (default: `No {noun} found`) — for filter-aware copy once filtering exists. */
  emptyTitle?: string;
  /** Label for an optional empty-state action button. Renders only when paired with `onEmptyAction`. */
  emptyActionLabel?: string;
  /** Handler for the optional empty-state action button (e.g. a future "Clear all filters"). */
  onEmptyAction?: () => void;
  pageSize?: number;
  page: number;
  /**
   * `options.replace` distinguishes an explicit pager click (default, unset — the caller should
   * push a new history entry so browser back/forward step through previously-viewed pages) from
   * this component's own stale-page correction (passed `{ replace: true }` — a push there would
   * let browser Back restore the stale page and immediately re-trigger the same correction).
   */
  onPageChange: (page: number, options?: { replace?: boolean }) => void;
  /**
   * The caller's own filter that isn't a taxonomy facet (e.g. a Saved-only checkbox). With facets,
   * forwarded to `ContentFilters` to lead the rail, above the accordions, per the design — falls
   * back to its own row (desktop only) for a facet-free listing, which has no rail to lead.
   */
  primaryControls?: React.ReactNode;
  /** Extra controls beside `primaryControls` (e.g. a future sort control) — same row, desktop only. */
  trailingControls?: React.ReactNode;
  /**
   * The phone/tablet counterpart to `primaryControls`: forwarded to the drawer and rendered above
   * the facet accordions. The rail row is desktop-only, so without this a caller's own filter has
   * no home below `desktop:`.
   */
  drawerLeading?: React.ReactNode;
  /**
   * Active filters neither `value` nor `extraChips` accounts for — i.e. whatever `primaryControls`
   * and `drawerLeading` own. Counts toward "Filters (N)" and keeps Clear All live when the
   * caller's own control is the only thing active.
   */
  filterCountExtra?: number;
  /** Active facet terms as removable chips. Build these from `facets`/`value`; this component only renders them. */
  chips?: ContentListingChip[];
  /** Chips for state outside the facet/value pair (e.g. the active search term) — merged with `chips` in the same row. */
  extraChips?: ContentListingChip[];
  /** Clears every active filter, search term, and extra chip at once. Required if `chips`/`extraChips`/search can be non-empty. */
  onClearAll?: () => void;
  /**
   * Withholds the count line, chips, grid and pager while keeping the lockup and `belowHead` — for a
   * listing whose only item is already featured in `belowHead`, where an empty grid would misread.
   */
  showResults?: boolean;
  className?: string;
}

function ContentListingSkeleton({ noun, pageSize }: { noun: string; pageSize: number }) {
  const { t } = useTranslation('shared');
  const keys =
    pageSize === DEFAULT_PAGE_SIZE ? SKELETON_KEYS : Array.from({ length: pageSize }, (_, i) => `skeleton-${i}`);
  return (
    <div className="@container">
      <div
        role="status"
        aria-busy="true"
        aria-label={t('contentListing.loadingLabel', { noun })}
        className="grid grid-cols-1 gap-4 @[560px]:grid-cols-2 @[720px]:gap-6 @[980px]:grid-cols-3"
      >
        {keys.map((key) => (
          <div key={key} className="flex flex-col overflow-hidden rounded-xl border bg-card shadow-xs">
            <Skeleton className="aspect-[16/9] w-full rounded-none" />
            <div className="flex flex-1 flex-col gap-3 p-5">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-5 w-4/5" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-3 w-32" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ContentListingEmpty({
  noun,
  hint,
  icon,
  title,
  actionLabel,
  onAction,
}: {
  noun: string;
  hint: string;
  icon?: IconName;
  title?: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  const { t } = useTranslation('shared');
  return (
    <div className="flex flex-col items-center gap-6 rounded-xl border bg-card px-6 py-16 text-center">
      <div className="flex size-16 items-center justify-center rounded-full bg-secondary">
        <Icon name={icon ?? 'search'} size="xl" className="text-[26px] text-muted-foreground" />
      </div>
      <div className="flex max-w-md flex-col gap-2">
        <h3 className="text-lg font-semibold text-foreground">
          {title ?? t('contentListing.emptyTitleDefault', { noun })}
        </h3>
        <p className="text-base leading-relaxed text-muted-foreground">{hint}</p>
      </div>
      {actionLabel && onAction && (
        <Button className="min-h-11" onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </div>
  );
}

function ContentListingPager({
  page,
  pageCount,
  onChange,
}: {
  page: number;
  pageCount: number;
  onChange: (page: number) => void;
}) {
  const { t } = useTranslation('shared');
  const range = paginationRange(page, pageCount);
  const goTo = (target: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    // A "disabled" Previous/Next is only pointer-events-none/tabIndex=-1 — neither blocks every
    // activation vector (a screen reader's synthetic activate, or a direct .click() call), so this
    // guard is the actual backstop against an out-of-range page reaching the caller.
    if (target < 1 || target > pageCount) return;
    onChange(target);
  };

  return (
    <Pagination>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#"
            disabled={page === 1}
            onClick={goTo(page - 1)}
            label={t('contentListing.previous')}
          />
        </PaginationItem>
        {range.map((item, i) =>
          typeof item === 'number' ? (
            <PaginationItem key={item}>
              <PaginationLink href="#" isActive={item === page} onClick={goTo(item)}>
                {item}
              </PaginationLink>
            </PaginationItem>
          ) : (
            // biome-ignore lint/suspicious/noArrayIndexKey: gap markers repeat the same string on either side of the range
            <PaginationItem key={`${item}-${i}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ),
        )}
        <PaginationItem>
          <PaginationNext
            href="#"
            disabled={page === pageCount}
            onClick={goTo(page + 1)}
            label={t('contentListing.next')}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

/**
 * ContentListing — the shared library listing section: title/search lockup, the standing filter
 * rail (desktop) / drawer (phone and tablet), the active-filter chip row, the card grid, loading
 * skeleton, empty state, and a numbered pager.
 *
 * The rail-vs-drawer split is pure CSS (the `desktop:` variant, `--breakpoint-desktop: 1024px` in
 * `layout.css`) — both the rail and the drawer's trigger button are always mounted, and only one
 * is ever visible at a given width. This is deliberate: a JS-detected breakpoint swapping which
 * component tree mounts is the one thing this codebase's `ResponsiveModal` history warns against
 * (see its doc comment) — it creates a seam wherever the JS threshold disagrees with any CSS
 * breakpoint inside either swapped side. A plain viewport variant, not a container query: this is
 * a page-level layout decision (title/search lockup, rail-vs-drawer), which should track viewport
 * width directly — unlike the card grid below, whose available width shrinks once a rail sits
 * beside it and which container-queries for exactly that reason.
 *
 * Fully controlled: `page`/`onPageChange` are the caller's (typically a URL query param), and
 * `pageCount` is computed from `filteredCount` — the caller's total match count — never from
 * `cards.length`, since `cards` is only ever the current page's rows.
 */
export function ContentListing({
  noun,
  nounSingular,
  title,
  dek,
  above,
  belowHead,
  searchLabel,
  q = '',
  onQChange,
  facets = [],
  value = {},
  onFacetChange,
  cards,
  filteredCount,
  loading = false,
  emptyIcon,
  emptyHint,
  emptyTitle,
  emptyActionLabel,
  onEmptyAction,
  pageSize = DEFAULT_PAGE_SIZE,
  page,
  onPageChange,
  primaryControls,
  trailingControls,
  drawerLeading,
  filterCountExtra = 0,
  chips = [],
  extraChips = [],
  onClearAll,
  showResults = true,
  className,
}: ContentListingProps) {
  const locale = useLocale();
  const { t } = useTranslation('shared');
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const pageCount = Math.max(1, Math.ceil(filteredCount / pageSize));
  // A stale `page` (e.g. the caller hasn't re-clamped it yet after `filteredCount` shrank) would
  // otherwise fetch/receive zero `cards` for a page that doesn't exist, rendering the empty state
  // and hiding the pager even though results exist — stranding the member on a dead page. Render
  // against the clamped page and ask the caller to correct it, rather than reading that as "empty."
  const currentPage = Math.min(page, pageCount);
  // Gated on `!loading`: while a fetch is in flight, `filteredCount` is still the caller's
  // pre-fetch placeholder (typically 0, per RecipesPage), which would clamp `pageCount` to 1 and
  // misread every deep-linked page > 1 as "stale" before the real count ever arrives.
  const pageOutOfRange = !loading && page > pageCount;
  React.useEffect(() => {
    if (pageOutOfRange) onPageChange(pageCount, { replace: true });
  }, [pageOutOfRange, pageCount, onPageChange]);
  const isEmpty = !loading && !pageOutOfRange && cards.length === 0;
  const showSkeleton = loading || pageOutOfRange;

  const hasFilters = facets.length > 0;
  const hasSearch = Boolean(searchLabel);
  const allChips = [...extraChips, ...chips];
  const activeCount = countSelected(value) + extraChips.length + filterCountExtra;
  // Also on `filterCountExtra` alone: a caller's own filter renders no chip (by design), but Clear
  // All lives in this row, so without it the only way to switch that filter off would be the
  // control itself.
  const showChipRow = allChips.length > 0 || filterCountExtra > 0;

  const countBody = (
    <Trans
      t={t}
      i18nKey="contentListing.countLine"
      values={{
        shown: cards.length.toLocaleString(locale),
        total: filteredCount.toLocaleString(locale),
        noun: t('contentListing.resultNoun', {
          count: filteredCount,
          singular: nounSingular ?? noun,
          plural: noun,
        }),
      }}
      components={{
        shown: <span className="font-semibold tabular-nums text-foreground" />,
        total: <span className="font-semibold tabular-nums text-foreground" />,
      }}
    />
  );

  // Stays mounted through loading transitions — a screen reader only announces aria-live updates
  // to a region already in the DOM, not one that unmounts and remounts fresh.
  const countLine = (
    <p
      aria-live="polite"
      className={cn('whitespace-nowrap text-sm text-muted-foreground', hasFilters && 'hidden desktop:block')}
    >
      {!showSkeleton && countBody}
    </p>
  );

  const searchField = hasSearch ? (
    <div className="relative">
      <Icon
        name="search"
        size="xs"
        className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        type="search"
        value={q}
        onChange={(e) => onQChange?.(e.target.value)}
        placeholder={searchLabel}
        aria-label={searchLabel}
        // Native `<input type="search">` clear button — not part of Chip, not styled by us
        // otherwise, and Chrome doesn't give it a pointer cursor by default.
        className="pl-9 [&::-webkit-search-cancel-button]:cursor-pointer"
      />
    </div>
  ) : null;

  // With facets, `primaryControls` moves into the `ContentFilters` rail instead (leading it, above
  // the accordions) — matching the design's rail layout. This row is its fallback home for a
  // facet-free listing, which has no rail for it to lead.
  const controlsRow =
    (!hasFilters && primaryControls) || trailingControls ? (
      <div className="hidden flex-wrap items-center gap-4 desktop:flex">
        {!hasFilters && primaryControls}
        {trailingControls}
      </div>
    ) : null;

  const filterTrigger = hasFilters ? (
    <Button variant="outline" className="min-h-11 shrink-0 gap-2 desktop:hidden" onClick={() => setDrawerOpen(true)}>
      <Icon name="sliders" size="xs" />
      {t('contentListing.filtersTrigger')}
      {activeCount ? t('contentFilters.selectedCount', { count: activeCount }) : ''}
    </Button>
  ) : null;

  const chipRow = showChipRow ? (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={onClearAll}
        className="min-h-11 text-sm font-semibold text-foreground underline decoration-from-font underline-offset-2 hover:text-primary"
      >
        {t('contentFilters.clearAll')}
      </button>
      {allChips.map((chip) => (
        <Chip
          key={chip.key}
          kind="filter"
          tone="secondary"
          icon={chip.icon}
          onRemove={chip.onRemove}
          srLabel={chip.srLabel}
        >
          {chip.label}
        </Chip>
      ))}
    </div>
  ) : null;

  const results = showSkeleton ? (
    <ContentListingSkeleton noun={noun} pageSize={pageSize} />
  ) : isEmpty ? (
    <ContentListingEmpty
      noun={noun}
      hint={emptyHint}
      icon={emptyIcon}
      title={emptyTitle}
      actionLabel={emptyActionLabel}
      onAction={onEmptyAction}
    />
  ) : (
    <div className="@container">
      <div className="grid grid-cols-1 gap-4 @[560px]:grid-cols-2 @[720px]:gap-6 @[980px]:grid-cols-3">
        {cards.map((card, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: href isn't guaranteed unique (placeholder/story fixtures share one); index is a stable tiebreaker within one page.
          <ContentCard key={`${card.href}-${i}`} {...card} />
        ))}
      </div>
    </div>
  );

  const meta = (
    <div className="flex flex-col gap-4">
      {controlsRow}
      {hasFilters && (
        <div className="flex items-center gap-3 border-y py-2 desktop:hidden">
          {filterTrigger}
          <p aria-live="polite" className="min-w-0 truncate text-sm text-muted-foreground">
            {!showSkeleton && countBody}
          </p>
        </div>
      )}
      {chipRow}
      {countLine}
    </div>
  );

  return (
    <div className={cn('flex flex-col gap-8', className)}>
      {above}

      <div
        className={cn(
          'grid grid-cols-1 gap-4 pt-3',
          hasSearch && 'desktop:grid-cols-3 desktop:items-end desktop:gap-6',
        )}
      >
        {/* Search sits to the right of the h1, baseline-aligned with it, at `desktop:` width; both
            go full width below that. */}
        <div className={cn('flex flex-col gap-2', hasSearch && 'desktop:col-span-2')}>
          <h1 className="text-h1 font-semibold text-foreground">{title}</h1>
          {dek && <p className="max-w-2xl text-base leading-relaxed text-muted-foreground">{dek}</p>}
        </div>
        {hasSearch && <div className="desktop:col-span-1">{searchField}</div>}
      </div>

      {belowHead}

      {!showResults ? null : hasFilters ? (
        <div className="grid grid-cols-1 gap-6 desktop:grid-cols-[280px_1fr] desktop:gap-8">
          <div className="hidden desktop:block">
            <ContentFilters facets={facets} value={value} onChange={onFacetChange} primaryControls={primaryControls} />
          </div>
          <div className="flex min-w-0 flex-col gap-6">
            {meta}
            {results}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {meta}
          {results}
        </div>
      )}

      {showResults && !showSkeleton && !isEmpty && pageCount > 1 && (
        <div className="flex flex-col items-center gap-3 pt-4">
          <ContentListingPager page={currentPage} pageCount={pageCount} onChange={onPageChange} />
        </div>
      )}

      {hasFilters && (
        <ContentFiltersDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          facets={facets}
          value={value}
          onChange={onFacetChange}
          onClearAll={() => onClearAll?.()}
          resultCount={filteredCount}
          leading={drawerLeading}
          extraActiveCount={filterCountExtra}
        />
      )}
    </div>
  );
}
